import { computed } from 'mobx';
import { inject, observer } from 'mobx-react';
import type { ReactElement } from 'react';
import type React from 'react';
import { Component } from 'react';
import type PermissionsStore from '../../../cache/stores/permissions-store';
import SecureMode from './enums/secure-mode';

type renderPropsCallback = (allowed: boolean) => ReactElement;

interface IProps {
  mode: SecureMode;
  rules?: Array<string> | object;
  authStore?: any;
  locationId?: number;
  fallback?: JSX.Element | null;
  permissions?: PermissionsStore;
  children: ReactElement | ReactElement[] | renderPropsCallback;
}

export const withSecure = <T extends U, U extends object>(
  Comp: React.ComponentType<U>,
  rules?: Array<string> | object,
  mode?: SecureMode
) => {
  const WrappedComponent: React.FC<T> = (props: T) => {
    return (
      <Secure mode={mode} rules={rules}>
        <Comp {...props} />
      </Secure>
    );
  };

  WrappedComponent.displayName = 'WithSecure';

  return WrappedComponent;
};

@inject('permissions', 'authStore')
@observer
class Secure extends Component<IProps> {
  static defaultProps = {
    rules: [],
    mode: SecureMode.AND
  };

  render() {
    let { fallback, children } = this.props;

    if (!this.show) {
      return fallback || null;
    }
    if (typeof children === 'function') {
      return <>{children(this.isSecure)}</>;
    }

    return <>{children}</>;
  }

  get rules() {
    let { rules } = this.props;
    if (Array.isArray(rules!)) {
      rules = rules.reduce((acc: any, rule: string) => {
        acc[rule] = () => {
          return true;
        };
        return acc;
      }, {});
    }

    return Object.entries(rules!);
  }

  @computed
  get show(): boolean {
    if (!this.rules || this.rules.length === 0) {
      return true;
    }

    const { permissions } = this.props;
    if (!permissions!.loaded) {
      return false;
    }
    if (!this.isSecure) {
      console.error('Failed permissions', this.rules, permissions);
    }
    return this.isSecure;
  }

  @computed
  get isSecure(): boolean {
    let { mode, locationId, permissions, authStore } = this.props;
    let id: number = locationId || authStore?.user?.location?.id;
    let rules = this.rules;
    let allowed = (rule: any) => {
      let [key, value] = rule;
      return permissions!.canDoAction(key, id) && value();
    };
    switch (mode) {
      case SecureMode.AND:
        return rules.every(allowed);
      case SecureMode.OR:
        return rules.some(allowed);
      case SecureMode.NOR:
        return !rules.every(allowed);
      default:
        throw new Error('Unknown secure mode');
    }
  }
}
interface ISecurityOr {
  actions: string[];
  children: ReactElement | ReactElement[];
  location?: number;
}

const SecurityOr = ({ actions, children, location }: ISecurityOr) => {
  return (
    <Secure rules={actions} mode={SecureMode.OR} locationId={location}>
      {children}
    </Secure>
  );
};

export { SecurityOr };

export default Secure;
